import React, { useCallback } from "react";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import {
    DA_Button,
    DA_ButtonColor,
} from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { useBottomSheet } from "@danishagro/shared/src/contexts/bottomSheet/bottomSheet.context";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { _DeleteUserBottomSheet } from "../../BottomSheets/DeleteUserBottomSheet/DeleteUserBottomSheet.component";
import { _EditUserBottomSheet } from "../../BottomSheets/EditUserBottomSheet/EditUserBottomSheet.component";
import S from "./UserDetails.module.scss";

interface UserDetailsProps {
    userName: string;
    userId: string;
    currentRole: string;
    invitedBy: string;
    customerNumbers: {
        cvr: string;
        name: string;
        number: string;
    }[];
    onRemove: () => void;
    onEdit: () => void;
    noFooterAction?: boolean;
}

export const _UserDetails = (props: UserDetailsProps) => {
    const { getDictionaryItem, getDictionaryString } = useTranslations();
    const { presentBottomSheet, dismissBottomSheet } = useBottomSheet();
    const { cvrNumber } = useAppData();

    const openDeleteBottomSheet = useCallback(() => {
        presentBottomSheet(
            <_DeleteUserBottomSheet
                id={props.userId}
                name={props.userName}
                onRemove={props.onRemove}
            />,
            {
                id: "delete-user",
                headerTitle: getDictionaryString("deleteUser", { uppercaseFirst: true }),
                contentPadding: true,
                snapPoints: ({ maxHeight }) => [maxHeight - 50],
            }
        );
    }, [getDictionaryString, presentBottomSheet, props.onRemove, props.userId, props.userName]);

    const openEditBottomSheet = useCallback(() => {
        presentBottomSheet(
            <_EditUserBottomSheet
                id={props.userId}
                name={props.userName}
                cvr={cvrNumber}
                role={props.currentRole}
                onSubmit={props.onEdit}
            />,
            {
                id: "edit-user",
                headerTitle: getDictionaryString("editUser", { uppercaseFirst: true }),
                contentPadding: true,
                snapPoints: ({ maxHeight }) => [maxHeight - 50],
            }
        );
    }, [
        cvrNumber,
        getDictionaryString,
        presentBottomSheet,
        props.currentRole,
        props.onEdit,
        props.userId,
        props.userName,
    ]);

    return (
        <>
            <div className={S.userDetails}>
                <div className={S.columns}>
                    <div className={S.column}>
                        <div className={S.roleLabel}>{getDictionaryItem("type")}:</div>
                        <div className={S.roleValue}>
                            {getDictionaryItem(props.currentRole, {
                                uppercaseFirst: true,
                            })}
                        </div>
                    </div>
                    {/* TODO When deleted users are shown again, incomment this*/}
                    {/* {props.invitedBy && (
                        <div className={S.column}>
                            <div className={S.roleLabel}>{getDictionaryItem("invitedBy")}:</div>
                            <div className={S.roleValue}>{props.invitedBy}</div>
                        </div>
                    )} */}
                </div>
            </div>
            <div>
                <div className={S.accountsLabel}>{getDictionaryItem("associatedAccount")}</div>
                <div className={S.associatedAccountsList}>
                    {props?.customerNumbers?.map((item) => (
                        <div key={item.number} className={S.associatedAccount}>
                            {item.name} ({item.number})
                        </div>
                    ))}
                </div>
            </div>
            <div className={S.buttonWrapper}>
                {props.noFooterAction ? (
                    <DA_Button
                        title={getDictionaryString("Close", {
                            uppercaseFirst: true,
                        })}
                        onClick={dismissBottomSheet}
                        className={S.primary}
                        color={DA_ButtonColor.Grey}
                        isGhost
                    />
                ) : (
                    <>
                        {props.currentRole !== "owner" && (
                            <DA_Button
                                title={getDictionaryString("deleteUser", {
                                    uppercaseFirst: true,
                                })}
                                onClick={openDeleteBottomSheet}
                                className={S.primary}
                                color={DA_ButtonColor.Alert}
                                isGhost
                            />
                        )}
                        <DA_Button
                            title={getDictionaryString("editUser")}
                            onClick={openEditBottomSheet}
                            className={S.secondary}
                        />
                    </>
                )}
            </div>
        </>
    );
};
